import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HairProductKey } from "~/api/models/HairProductKey";
import { SkinProductKey } from "~/api/models/SkinProductKey";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import { useFetchProductDetails } from "~/api/storyblok/useFetchProductDetails";
import OfferCard from "~/components/molecules/OfferCard/OfferCard";
import useHairProducts from "~/hooks/useHairProducts";
import useSkinProducts from "~/hooks/useSkinProducts";
import { Product } from "~/stores/checkout/product";

type FreeProductKey =
  | SkinProductKey.freeBotox
  | HairProductKey.freePrp
  | HairProductKey.freeMesotherapy;

type FreeProductCardProps = {
  productKey: FreeProductKey;
};

const FreeProductCard = ({ productKey }: FreeProductCardProps) => {
  const intl = useIntl();
  const { opportunityId } = useParams();

  const { data: opportunity, isLoading: loadingOpportunity } =
    useFetchOpportunity({
      id: opportunityId as string,
    });

  const { data: productDetails, isLoading: loadingProductDetails } =
    useFetchProductDetails(productKey);

  const { freePrpProduct, freeMesotherapyProduct } = useHairProducts();
  const { freeBotoxProduct } = useSkinProducts();

  const currentProduct: Product | undefined = useMemo(() => {
    switch (productKey) {
      case SkinProductKey.freeBotox:
        return freeBotoxProduct;
      case HairProductKey.freePrp:
        return freePrpProduct;
      case HairProductKey.freeMesotherapy:
        return freeMesotherapyProduct;
      default:
        return undefined;
    }
  }, [productKey, freeBotoxProduct, freePrpProduct, freeMesotherapyProduct]);

  const cardTitle = useMemo(() => {
    switch (productKey) {
      case SkinProductKey.freeBotox:
        return "Free Botox";
      case HairProductKey.freePrp:
        return "Free PRP";
      case HairProductKey.freeMesotherapy:
        return "Free Mesotherapy";
      default:
        return "Free Product";
    }
  }, [productKey, intl]);

  // Only render if the product is actually selected
  if (!currentProduct) {
    return null;
  }

  return (
    <AnimatePresence>
      {!loadingOpportunity && (
        <OfferCard
          opportunityGender={opportunity?.gender}
          image={productDetails?.image}
          loadingDetails={loadingProductDetails}
          title={cardTitle}
          price={intl.formatMessage({ id: "pricing.amount" }, { amount: 0 })}
          className="!border-green-200 bg-green-50"
        />
      )}
    </AnimatePresence>
  );
};

export default FreeProductCard;
